import React from 'react';

import { Grid, TextField, Typography, Button, Icon, Chip, FormControl, FormControlLabel, Checkbox } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import { OrganogramModel, organogramModelInitial } from '../../../../Models/OrganogramModel';
import { ClientContext } from '../../ClientContext';
import { EditAction } from '../../../../shared/enum/EditAction';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { theme } from '../../../../theme';

interface IOgranogramModal {
    organogramLevelText: string
    itemText: string
    forText: string
    destinationText: string
    originOrganogram: OrganogramModel | null    
    showAddOrganogramDialog: boolean
    setShowAddOrganogramDialog: (value: boolean) => void, 
}

const useStyles = makeStyles(() => createStyles({
    modalChipItem: {
        borderStyle: 'solid',
        borderWidth: '2px'
    }
}));

export function ClientOrganogramModal({
    organogramLevelText,
    itemText,
    forText,
    destinationText,
    originOrganogram,    
    showAddOrganogramDialog,
    setShowAddOrganogramDialog}: IOgranogramModal) {
    const classes = useStyles();

    const clientContext = React.useContext(ClientContext);  

    const [modalOrganogramList, setModalOrganogramList] = React.useState<OrganogramModel[]>([]);
    const [inputModalItem, setInputModalItem] = React.useState<string>('');
    const [isConcurrent, setIsConcurrent] = React.useState<boolean>(false);
    const [inputError, setInputError] = React.useState<boolean>(false);

    const handleAddModalOrganogram = () => {
        if (!hasModalError()) {
            const organogramList : OrganogramModel[] = JSON.parse(JSON.stringify(clientContext.flatOrganogram));

            modalOrganogramList?.forEach((organogram : OrganogramModel) => {
                if (organogram) 
                    organogramList.push(organogram);                        
            });
    
            clientContext.setFlatOrganogram(organogramList);
            
            clearModal();
        }
    };

    const hasModalError = () => {
        const modalList = JSON.parse(JSON.stringify(modalOrganogramList));
        const inputOrganogramError = !(modalList && modalList.length > 0);
        setInputError(inputOrganogramError);
        return inputOrganogramError;
    };

    const clearModal = () => {        
        setShowAddOrganogramDialog(false); 
        setModalOrganogramList([]);
        setInputModalItem('');
        setIsConcurrent(false);
        setInputError(false);
    };

    const handleAddModalItem = () => {
        const item = inputModalItem.split(';');
        const listOrganogram = JSON.parse(JSON.stringify(modalOrganogramList));
        let id = clientContext.organogramId;
        
        item.forEach(i => {
            id += 1;
            if (i.trim() != '') {
                const organogram: OrganogramModel = { 
                    ...organogramModelInitial, 
                    id: id,
                    clientId: clientContext.client.id,
                    description: i,
                    concurrent: isConcurrent,
                    active: true,
                    editAction: EditAction.INSERT
                };

                if (originOrganogram) 
                    organogram.parentId = originOrganogram.id;

                listOrganogram.push(organogram);
            }
        });

        clientContext.setOrganogramId(id);
        setModalOrganogramList(listOrganogram);
        setInputModalItem('');
    };

    const handleDeleteModalItem = (index: number) => {
        const listOrganogram = JSON.parse(JSON.stringify(modalOrganogramList));   
        listOrganogram.splice(index, 1);

        setModalOrganogramList(listOrganogram);
    };

    const getOrganogramModal = () => {
        return (
            <Grid 
                container 
                spacing={2}
                justify="space-between"
                alignItems="center">
                <Grid item>
                    <Typography>Inserir {itemText} que deseja adicionar para {forText}</Typography>
                </Grid>
                { getModalInsertItem() }
                { getModalIsConcurrent() }
                { getModalAddButton() }
                { modalOrganogramList && modalOrganogramList.length > 0 && getModalList() }
            </Grid>
        );
    };

    const getModalInsertItem = () => {
        return (
            <Grid item xs={12} md={6}>
                <TextField
                    value={inputModalItem}
                    variant="outlined"
                    label={destinationText}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => { setInputModalItem(event.target.value); }}                            
                    fullWidth       
                    error={inputError && !(modalOrganogramList && modalOrganogramList.length > 0)}
                    helperText={(inputError && !(modalOrganogramList && modalOrganogramList.length > 0)) &&
                        'É preciso inserir ao menos um item.'} />
            </Grid>
        );
    };

    const getModalIsConcurrent = () => { 
        return (
            <Grid item xs={12} md={3}>
                <FormControl component="fieldset">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isConcurrent}
                                onChange={() => setIsConcurrent(!isConcurrent)}
                                name="Concorrente"
                                color="primary" />
                        }
                        label="Concorrente" />
                </FormControl>
            </Grid>
        );
    };

    const getModalAddButton = () => {
        return (
            <Grid item xs={12} md={3}>
                <Button
                    onClick={handleAddModalItem}
                    variant="outlined"
                    size='large'>
                    ADICIONAR
                </Button>   
            </Grid>
        );
    };

    const getModalList = () => {
        return (
            <>
                { getModalLegend() }
                { getModalRender() }
            </>
        );
    };

    const getModalLegend = () => {
        return (
            <Grid item xs={12}>
                <Grid 
                    container 
                    direction="row"
                    justify="flex-start"
                    alignItems="center">
                    <Grid item xs={2}>
                        <Typography variant='subtitle2'>Legenda: </Typography>
                    </Grid>
                    <Grid item xs={1}>
                        <Chip
                            label='⠀⠀⠀'  // Caracter invisível, NÃO substituir por espaço
                            size="small" 
                            variant='outlined'
                            className={classes.modalChipItem}
                            color='primary' />
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant='caption'>Concorrente</Typography>
                    </Grid>
                    <Grid item xs={1}>
                        <Chip
                            label='⠀⠀⠀'  // Caracter invisível, NÃO substituir por espaço
                            size="small" 
                            variant='outlined'
                            className={classes.modalChipItem} />
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant='caption'>Não é concorrente</Typography>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const getModalRender = () => {
        return (
            <Grid item xs={12}>
                <Grid container spacing={2}>
                    {modalOrganogramList.map((organogram : OrganogramModel, index: number) => {
                        if (organogram.active)
                            return (
                                <Grid item key={organogram.description}>
                                    <Chip
                                        key={organogram.id}
                                        label={organogram.description}                        
                                        deleteIcon={<Icon>close</Icon>}
                                        onDelete={() => { handleDeleteModalItem(index); }}
                                        onClick={() => { handleDeleteModalItem(index); }}
                                        className={classes.modalChipItem}
                                        variant='outlined'
                                        color={organogram.concurrent ? 'primary' : 'default'} />
                                </Grid>
                            );
                    })}
                </Grid>
            </Grid>
        );
    };

    return (     
        <BasicConfirmDialog
            title="Cadastro de organograma"
            description={organogramLevelText}
            textFirstButton= 'Descartar'
            handleFirstButton={clearModal}
            textSecondButton= 'Salvar'
            handleSecondButton={handleAddModalOrganogram}
            open={showAddOrganogramDialog}
            children={ getOrganogramModal() }
            cssProperties={{ 
                minWidth: '700px',
                maxWidth: '700px',
                colorFirstButton: theme.palette.primary.main,
                justifyButtons: 'flex-end',
                titleVariant: 'h4',
                descriptionVariant: 'body1'
            }} />
    );
}